/**
 * Client-side image compression for mobile uploads.
 * iPhone photos are typically 8-12 MB, exceeding our backend 15 MB limit
 * and causing slow / failed uploads on mobile networks.
 *
 * - Skip non-image files (e.g. PDFs)
 * - Skip files already small (< 700 KB)
 * - Resize so that the longer edge is <= 2048 px (preserves quality)
 * - Re-encode as JPEG quality 0.85
 * - Falls back to the original file on any error
 */

const MAX_DIMENSION = 1280;
const QUALITY = 0.75;
const SKIP_BELOW_BYTES = 350 * 1024;

export async function compressImage(file) {
  if (!file || !(file instanceof File)) return file;
  if (!file.type || !file.type.startsWith("image/")) return file;
  // HEIC images are decoded natively by iOS Safari when delivered via <input file>;
  // for other browsers, fall back to original (server still accepts).
  if (file.size <= SKIP_BELOW_BYTES) return file;

  try {
    const dataUrl = await fileToDataURL(file);
    const img = await loadImage(dataUrl);

    const { width: w, height: h } = scaleToFit(img.naturalWidth, img.naturalHeight, MAX_DIMENSION);

    // If no resize needed AND already JPEG, return original to avoid quality loss
    if (w === img.naturalWidth && h === img.naturalHeight && file.type === "image/jpeg") {
      return file;
    }

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, w, h);

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", QUALITY),
    );
    if (!blob) return file;

    // If compression made it bigger (rare for tiny images), keep original
    if (blob.size >= file.size) return file;

    const newName = (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], newName, { type: "image/jpeg", lastModified: Date.now() });
  } catch {
    return file;
  }
}

function fileToDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function scaleToFit(w, h, max) {
  if (w <= max && h <= max) return { width: w, height: h };
  const ratio = w >= h ? max / w : max / h;
  return { width: Math.round(w * ratio), height: Math.round(h * ratio) };
}
